/**
 * Utility functions for handling image URLs
 * Handles transition from local server paths to Digital Ocean Spaces URLs
 * Includes cache-busting for image updates
 */



/**
 * Get the correct image URL whether it's a local path or Digital Ocean Spaces URL
 * @param {string} imagePath - The image path from the database
 * @param {string} defaultImage - Default image path if no image is provided
 * @returns {string} - The complete image URL with cache-busting
 */
export const getImageUrl = (imagePath, defaultImage = null) => {
  // If no image path provided, return default
  if (!imagePath) {
    return defaultImage || '/assets/course-place1.png';
  }

  // If it's already a full URL (Digital Ocean Spaces), return as is
  if (imagePath?.startsWith('http://') || imagePath?.startsWith('https://')) {
    return imagePath;
  }

  // If it's a local path, construct with server URL
  return `${process.env.REACT_APP_SERVER}/${imagePath}`;
};

/**
 * Get avatar URL with fallback to default avatars
 * @param {string} avatarPath - The avatar path from the database
 * @param {number} gender - Gender (0 for male, 1 for female)
 * @returns {string} - The complete avatar URL with cache-busting
 */
export const getAvatarUrl = (avatarPath, gender = null) => {
  const maleAvatar = '/assets/image/user/male.png';
  const femaleAvatar = '/assets/image/user/female.png';
  const defaultUserAvatar = '/assets/image/user/User-avatar.svg.png';

  // If no avatar provided, return gender-specific default
  if (!avatarPath) {
    if (gender === 1) return femaleAvatar;
    if (gender === 0) return maleAvatar;
    return defaultUserAvatar;
  }

  // If it's already a full URL (Digital Ocean Spaces), return as is
  if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://')) {
    return avatarPath;
  }

  // If it's a local path, construct with server URL
  return `${process.env.REACT_APP_SERVER}/${avatarPath}`;
};

/**
 * Get course image URL
 * @param {string} imagePath - The course image path
 * @returns {string} - The complete course image URL
 */
export const getCourseImageUrl = (imagePath) => {
  return getImageUrl(imagePath, '/assets/course-place1.png');
};

export const getCourseImageDisplayUrlWithCleanup = (imageData) => {
  if (imageData instanceof File) {
    const url = URL.createObjectURL(imageData);
    return {
      url,
      cleanup: () => URL.revokeObjectURL(url)
    };
  }
  
  if (typeof imageData === 'string') {
    return {
      url: getCourseImageUrl(imageData),
      cleanup: () => {} 
    };
  }
  
  return {
    url: '/assets/course-place1.png',
    cleanup: () => {}
  };
};
/**
 * Get course file URL (PDF)
 * @param {string} filePath - The course file path
 * @returns {string} - The complete course file URL
 */
export const getCourseFileUrl = (filePath) => {
  return getImageUrl(filePath, '/default-file.pdf');
}; 